import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useCatalog, useOpenApplication } from '../hooks/useCatalog';
import { AppIcon } from '../components/AppIcon';
import { EnvironmentBadge, NetworkBadge } from '../components/Badges';
import { EmptyState, ErrorState, LoadingState } from '../components/Feedback';
import { ACCESS_SOURCE_LABELS, appColor, formatDate } from '../lib/utils';

export function MyAccessPage() {
    const openApplication = useOpenApplication();

    const {
        data: catalog,
        isLoading,
        isError,
        error,
        refetch,
    } = useCatalog({
        search: '',
        category: null,
        environment: null,
        network: null,
        favoritesOnly: false,
    });

    const applications = catalog?.applications ?? [];

    return (
        <div className="space-y-8">
            <header>
                <h1 className="text-2xl font-bold text-[var(--color-text)]">Mes accès</h1>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    Les applications auxquelles vous avez droit.
                </p>
            </header>

            <section className="space-y-3">
                <h2 className="text-lg font-semibold text-[var(--color-text)]">
                    Applications accessibles
                    <span className="ml-2 text-sm font-normal text-[var(--color-text-muted)] tabular-nums">
                        {applications.length}
                    </span>
                </h2>

                {isLoading ? (
                    <LoadingState />
                ) : isError ? (
                    <ErrorState
                        message={error instanceof Error ? error.message : 'Chargement impossible.'}
                        onRetry={() => void refetch()}
                    />
                ) : applications.length === 0 ? (
                    <EmptyState
                        title="Aucun accès pour le moment"
                        description="Parcourez le catalogue pour découvrir les applications du parc. Un administrateur peut vous donner accès à celles dont vous avez besoin."
                        action={
                            <Link to="/" className="btn btn-primary">
                                Voir le catalogue
                            </Link>
                        }
                    />
                ) : (
                    <div className="card overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th scope="col">Application</th>
                                    <th scope="col">Origine du droit</th>
                                    <th scope="col">Échéance</th>
                                    <th scope="col" className="text-right">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {applications.map((application) => (
                                    <tr key={application.id}>
                                        <td>
                                            <div className="flex items-center gap-2.5">
                                                <span
                                                    className="app-swatch flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                                                    style={appColor(application.color)}
                                                    aria-hidden
                                                >
                                                    <AppIcon
                                                        name={application.icon}
                                                        className="w-4 h-4"
                                                    />
                                                </span>
                                                <div className="min-w-0">
                                                    <Link
                                                        to={`/applications/${application.slug}`}
                                                        className="font-medium text-[var(--color-text)] hover:text-[var(--color-primary)]"
                                                    >
                                                        {application.name}
                                                    </Link>
                                                    <div className="flex gap-1 mt-0.5">
                                                        {[
                                                            ...new Set(
                                                                application.instances.map(
                                                                    (i) => i.environment
                                                                )
                                                            ),
                                                        ].map((env) => (
                                                            <EnvironmentBadge
                                                                key={env}
                                                                environment={env}
                                                            />
                                                        ))}
                                                        {[
                                                            ...new Set(
                                                                application.instances.map(
                                                                    (i) => i.network
                                                                )
                                                            ),
                                                        ].map((network) => (
                                                            <NetworkBadge
                                                                key={network}
                                                                network={network}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-[var(--color-text-secondary)] text-xs">
                                            {application.accessSource
                                                ? ACCESS_SOURCE_LABELS[application.accessSource]
                                                : '-'}
                                        </td>
                                        <td className="text-[var(--color-text-secondary)]">
                                            {application.accessExpiresAt
                                                ? formatDate(application.accessExpiresAt)
                                                : 'Sans limite'}
                                        </td>
                                        <td className="text-right">
                                            {application.instances.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => openApplication(application)}
                                                    className="btn btn-ghost p-1.5"
                                                    aria-label={`Ouvrir ${application.name}`}
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
